import { useCallback, useEffect, useState } from 'react';
import { AttestationBadge } from './Community.tsx';
import { type DashboardTeam, TeamPanels } from './MetaDashboard.tsx';

// Community-team page. Same dashboard panels as a user's own saved team
// (KPIs, speed tier, both damage matrices, predicted leads, lead-pair
// dashboard, similar tournaments) but loaded from /community-team/:id
// instead of /team/:id. A header strip carries the rental code, peer
// attestation badge, and works/broken buttons.

type AttestMeta = {
  worksCount: number;
  brokenCount: number;
  viewerAttestation: 'works' | 'broken' | null;
  replicaCode: string | null;
  blurb: string | null;
  archetypeTags: string[] | null;
  authorDisplayName: string | null;
  createdAt: string;
  isOwn: boolean;
};

function navTo(path: string) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function backToCommunity() {
  if (window.history.length > 1) window.history.back();
  else navTo('/community');
}

export function CommunityTeam({ teamId }: { teamId: number }) {
  const [dashboard, setDashboard] = useState<DashboardTeam | null>(null);
  const [meta, setMeta] = useState<AttestMeta | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [signedIn, setSignedIn] = useState<boolean | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const [dashRes, metaRes] = await Promise.all([
        fetch(`/api/dashboards/community-team/${teamId}`, { credentials: 'include' }),
        fetch(`/api/community/teams/${teamId}`),
      ]);
      if (dashRes.status === 401) {
        setErr('unauthorized');
        return;
      }
      if (dashRes.status === 404 || metaRes.status === 404) {
        setErr('not_found');
        return;
      }
      const dashJson = (await dashRes.json()) as DashboardTeam & { ok: boolean };
      const metaJson = (await metaRes.json()) as { team: AttestMeta };
      if (!dashJson.ok) {
        setErr('load_failed');
        return;
      }
      setDashboard(dashJson);
      setMeta(metaJson.team);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed to load');
    }
  }, [teamId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    fetch('/api/auth/me')
      .then((r) => r.json())
      .then((j) => setSignedIn(j?.user != null))
      .catch(() => setSignedIn(false));
  }, []);

  async function setAttestation(kind: 'works' | 'broken') {
    if (!meta || busy) return;
    setBusy(true);
    try {
      const isToggleOff = meta.viewerAttestation === kind;
      if (isToggleOff) {
        await fetch(`/api/community/teams/${teamId}/attest`, { method: 'DELETE' });
      } else {
        await fetch(`/api/community/teams/${teamId}/attest`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ kind }),
        });
      }
      // Refetch only the community meta — the heavy dashboard payload is
      // unaffected by attestation changes.
      const m = await fetch(`/api/community/teams/${teamId}`).then((r) => r.json());
      setMeta(m.team);
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    if (!meta || !dashboard) return;
    if (!window.confirm(`Delete "${dashboard.team.name}" from the community feed?`)) return;
    setBusy(true);
    try {
      const r = await fetch(`/api/community/teams/${teamId}`, { method: 'DELETE' });
      if (r.ok) navTo('/community');
    } finally {
      setBusy(false);
    }
  }

  if (err === 'unauthorized') {
    return (
      <CenteredMessage>
        <p style={{ color: 'var(--fg-2)' }}>Sign in to view community team dashboards.</p>
        <a
          href="/account"
          className="caps mono"
          style={{
            fontSize: 10,
            padding: '8px 14px',
            background: 'var(--accent-1)',
            color: 'var(--bg-0)',
            border: '1px solid var(--accent-line)',
            borderRadius: 3,
            letterSpacing: '0.08em',
            textDecoration: 'none',
          }}
        >
          sign in →
        </a>
      </CenteredMessage>
    );
  }

  if (err === 'not_found') {
    return (
      <CenteredMessage>
        <BackButton />
        <h1 style={{ marginTop: 24, fontFamily: 'var(--font-serif)', fontWeight: 400 }}>
          Team not found.
        </h1>
      </CenteredMessage>
    );
  }

  if (err) {
    return (
      <CenteredMessage>
        <BackButton />
        <p style={{ color: 'var(--neg)', fontSize: 13 }}>Could not load team. {err}</p>
      </CenteredMessage>
    );
  }

  if (!dashboard || !meta) {
    return (
      <CenteredMessage>
        <BackButton />
        <span className="caps mono" style={{ color: 'var(--fg-4)', fontSize: 10 }}>
          loading dashboard…
        </span>
      </CenteredMessage>
    );
  }

  return (
    <div
      style={{
        maxWidth: 1320,
        margin: '0 auto',
        padding: '20px 24px 60px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      <BackButton />

      <AttestationStrip
        teamName={dashboard.team.name}
        meta={meta}
        signedIn={signedIn}
        busy={busy}
        onAttest={(k) => void setAttestation(k)}
        onDelete={() => void onDelete()}
      />

      <TeamPanels
        teamData={dashboard}
        err={null}
        leadPairEndpoint={`/api/dashboards/community-leadpair/${teamId}`}
      />
    </div>
  );
}

function CenteredMessage({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        maxWidth: 720,
        margin: '0 auto',
        padding: 32,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {children}
    </div>
  );
}

function BackButton() {
  return (
    <button
      type="button"
      onClick={backToCommunity}
      className="caps mono"
      style={{
        alignSelf: 'flex-start',
        fontSize: 10,
        color: 'var(--fg-3)',
        background: 'transparent',
        border: '1px solid var(--line-1)',
        padding: '4px 10px',
        borderRadius: 3,
        cursor: 'pointer',
        letterSpacing: '0.08em',
      }}
    >
      ← back to community
    </button>
  );
}

function AttestationStrip({
  teamName,
  meta,
  signedIn,
  busy,
  onAttest,
  onDelete,
}: {
  teamName: string;
  meta: AttestMeta;
  signedIn: boolean | null;
  busy: boolean;
  onAttest: (kind: 'works' | 'broken') => void;
  onDelete: () => void;
}) {
  const [copied, setCopied] = useState(false);

  async function copyCode() {
    if (!meta.replicaCode) return;
    try {
      await navigator.clipboard.writeText(meta.replicaCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — user can long-press the code */
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: 14,
        background: 'var(--bg-1)',
        border: '1px solid var(--line-1)',
        borderRadius: 4,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          gap: 8,
          fontSize: 12,
          color: 'var(--fg-3)',
        }}
      >
        <span
          className="caps mono"
          style={{ fontSize: 9.5, color: 'var(--fg-4)', letterSpacing: '0.1em' }}
        >
          community team
        </span>
        <span>·</span>
        <span style={{ color: 'var(--fg-2)' }}>{teamName}</span>
        <span>·</span>
        <span>by {meta.authorDisplayName ?? 'anonymous'}</span>
        <span>·</span>
        <span>{new Date(meta.createdAt).toLocaleDateString()}</span>
        {meta.archetypeTags?.map((tag) => (
          <span
            key={tag}
            className="caps mono"
            style={{
              fontSize: 9,
              color: 'var(--fg-3)',
              padding: '1px 5px',
              border: '1px solid var(--line-1)',
              borderRadius: 2,
              letterSpacing: '0.08em',
            }}
          >
            {tag}
          </span>
        ))}
      </div>

      {meta.blurb && (
        <p style={{ margin: 0, color: 'var(--fg-2)', fontSize: 13, lineHeight: 1.5 }}>
          {meta.blurb}
        </p>
      )}

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        {meta.replicaCode ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span
              className="caps mono"
              style={{ fontSize: 9.5, color: 'var(--fg-4)', letterSpacing: '0.1em' }}
            >
              rental code
            </span>
            <span
              className="mono"
              style={{
                fontSize: 14,
                color: 'var(--fg-1)',
                letterSpacing: '0.06em',
                padding: '3px 8px',
                background: 'var(--bg-2)',
                border: '1px solid var(--line-1)',
                borderRadius: 3,
              }}
            >
              {meta.replicaCode}
            </span>
            <button
              type="button"
              onClick={() => void copyCode()}
              className="caps mono"
              style={{
                fontSize: 9.5,
                padding: '3px 8px',
                background: 'transparent',
                border: '1px solid var(--line-1)',
                color: 'var(--fg-2)',
                borderRadius: 3,
                cursor: 'pointer',
                letterSpacing: '0.08em',
              }}
            >
              {copied ? 'copied' : 'copy'}
            </button>
          </div>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--fg-4)', fontStyle: 'italic' }}>
            no rental code — use the team export below
          </span>
        )}

        <AttestationBadge works={meta.worksCount} broken={meta.brokenCount} />

        {!meta.isOwn && signedIn === null && (
          <span className="caps mono" style={{ fontSize: 9.5, color: 'var(--fg-4)' }}>
            loading…
          </span>
        )}
        {!meta.isOwn && signedIn === true && (
          <div style={{ display: 'flex', gap: 6 }}>
            <AttestButton
              label="works"
              tone="pos"
              active={meta.viewerAttestation === 'works'}
              disabled={busy}
              onClick={() => onAttest('works')}
            />
            <AttestButton
              label="broken"
              tone="neg"
              active={meta.viewerAttestation === 'broken'}
              disabled={busy}
              onClick={() => onAttest('broken')}
            />
          </div>
        )}
        {!meta.isOwn && signedIn === false && (
          <a
            href="/account"
            className="caps mono"
            style={{
              fontSize: 9.5,
              color: 'var(--accent-2)',
              letterSpacing: '0.08em',
            }}
          >
            sign in to mark works/broken →
          </a>
        )}
        {meta.isOwn && (
          <button
            type="button"
            onClick={onDelete}
            disabled={busy}
            className="caps mono"
            style={{
              fontSize: 9.5,
              padding: '4px 10px',
              color: 'var(--neg)',
              background: 'transparent',
              border: '1px solid var(--neg)',
              borderRadius: 3,
              letterSpacing: '0.08em',
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.5 : 1,
              marginLeft: 'auto',
            }}
          >
            delete
          </button>
        )}
      </div>
    </div>
  );
}

function AttestButton({
  label,
  tone,
  active,
  disabled,
  onClick,
}: {
  label: string;
  tone: 'pos' | 'neg';
  active: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  const color = tone === 'pos' ? 'var(--pos)' : 'var(--neg)';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="caps mono"
      style={{
        fontSize: 10,
        padding: '5px 10px',
        background: active ? color : 'transparent',
        color: active ? 'var(--bg-0)' : color,
        border: `1px solid ${color}`,
        borderRadius: 3,
        letterSpacing: '0.08em',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
      }}
    >
      {label}
    </button>
  );
}
